import React, { useState, useEffect } from "react";
import { useFirebase } from "../context/firebase.context";

const SchoolArticles = () => {
    const { getArticles, getStudentsBySchool, deleteStudent, getImageURL } = useFirebase();
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [students, setStudents] = useState([]);

    // Fetch all unique school names on component mount
    useEffect(() => {
        const fetchSchools = async () => {
            const articlesSnapshot = await getArticles();
            const schoolNames = Array.from(
                new Set(articlesSnapshot.docs.map((doc) => doc.data().school))
            );
            setSchools(schoolNames);
        };

        fetchSchools();
    }, [getArticles]);

    // Fetch students when a school is selected
    useEffect(() => {
        const fetchStudents = async () => {
            if (selectedSchool) {
                const studentsData = await getStudentsBySchool(selectedSchool);

                // Fetch image URLs for each student
                const studentsWithImages = await Promise.all(
                    studentsData.map(async (student) => {
                        const imageURL = await getImageURL(student.imageURL);
                        return { ...student, imageURL }; // Add the fetched image URL to the student object
                    })
                );

                setStudents(studentsWithImages);
            }
        };

        fetchStudents();
    }, [selectedSchool, getStudentsBySchool, getImageURL]);

    // Handle deleting a single student
    const handleDeleteStudent = async (studentId, imageURL) => {
        try {
            await deleteStudent(studentId, imageURL);
            setStudents((prevStudents) => prevStudents.filter((student) => student.id !== studentId));
            console.log("Student deleted successfully.");
        } catch (error) {
            console.error("Error deleting student:", error);
        }
    };

    // Delete a school and all its students
    const deleteSchool = async (schoolName) => {
        try {
            const studentsData = await getStudentsBySchool(schoolName);
            for (const student of studentsData) {
                await deleteStudent(student.id, student.imageURL);
            }
            setSchools((prevSchools) => prevSchools.filter((school) => school !== schoolName));
            setSelectedSchool(null);
            setStudents([]);
            console.log(`School ${schoolName} and its students deleted successfully.`);
        } catch (error) {
            console.error("Error deleting school and students:", error);
        }
    };

    return (
        <div className="container my-4">
            <h2 className="text-center mb-5">Schools</h2>

            <div className="row row-cols-1 row-cols-md-3 g-4 mb-5">
                {schools.map((school, index) => (
                    <div key={index} className="col">
                        <div className="card shadow-sm h-100">
                            <div className="card-body d-flex flex-column">
                                <h5 className="card-title text-dark">{school}</h5>

                                {/* View Students Button */}
                                <button
                                    onClick={() => setSelectedSchool(school)}
                                    className="btn btn-success mt-3"
                                >
                                    View Students
                                </button>

                                {/* Delete School Button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteSchool(school);
                                    }}
                                    className="btn btn-danger mt-2"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {selectedSchool && (
                <div>
                    <h3 className="text-center text-primary mb-4">
                        Students from <strong>{selectedSchool}</strong>
                    </h3>

                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                        {students.map((student) => (
                            <div key={student.id} className="col">
                                <div className="card shadow-sm h-100">
                                    {/* Student Image */}
                                    <img
                                        src={student.imageURL}
                                        alt={student.name}
                                        className="card-img-top"
                                        style={{
                                            objectFit: "contain",
                                            height: "200px",
                                        }}
                                    />
                                    <div className="card-body d-flex flex-column">
                                        <h5 className="card-title text-dark mb-2">{student.name}</h5>
                                        <p className="card-text text-secondary mb-1">
                                            Class: <strong>{student.classs}</strong>
                                        </p>
                                        <p className="card-text text-secondary mb-1">
                                            Rank: <strong>{student.rank}</strong>
                                        </p>
                                        <p className="card-text text-secondary mb-3">
                                            Student ID: <strong>{student.studID}</strong>
                                        </p>
                                        <button
                                            onClick={() => handleDeleteStudent(student.id, student.imageURL)}
                                            className="btn btn-danger mt-auto"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchoolArticles;
